import { motion } from "framer-motion";
import { Smartphone, Layers, Users, Crown, Zap, Bot, ArrowDown, ClipboardCheck, Activity, Eye } from "lucide-react";
import { PageHero, CTASection } from "../components/site/Shared";
import { Reveal } from "../components/site/Reveal";
import { CommandCenterMockup } from "../components/site/Mockups";
import { Zoomable } from "../components/site/Zoomable";

const flow = ["Teacher App", "Real-Time Data", "PARTECON Operating Layer", "Students · Parents · Principal · Admin", "Real-Time Visibility", "Action · Automation · Intelligence"];

const steps = [
  {
    n: "01", icon: Smartphone, tone: "#6366F1", title: "The Teacher runs the class",
    body: "A teacher conducts the class through the PARTECON Teacher App — capturing class execution, attendance, lesson activity, homework, End-of-Class reporting and observations. Operational data is created exactly where school activity happens.",
    chips: ["Class execution", "Attendance", "End-of-Class (EOC)", "Homework & observations"],
  },
  {
    n: "02", icon: Layers, tone: "#5B7CFA", title: "The PARTECON operating layer",
    body: "That information moves into the PARTECON operating layer, which connects and structures it in real time — turning fragmented moments into one continuous operational record.",
    chips: ["Real-time structuring", "One data layer", "Continuity across the school"],
  },
  {
    n: "03", icon: Users, tone: "#22C55E", title: "Students & parents stay connected",
    body: "Relevant information becomes available to students and parents through their own PARTECON experiences — creating continuity from classroom → student → parent instead of scattered messages.",
    chips: ["Student experience", "Parent experience", "Governed communication"],
  },
  {
    n: "04", icon: Crown, tone: "#8B5CF6", title: "Leadership gets real-time visibility",
    body: "The Principal Command Center shows what is happening across the school as it happens — classes running, attendance, EOC compliance, alerts, performance and the tasks that need attention.",
    chips: ["Classes running", "AHI", "Alerts & tasks", "Teacher & student performance"], visual: "command",
  },
  {
    n: "05", icon: Eye, tone: "#F5B93C", title: "The school acts",
    body: "PARTECON doesn't just display information — it surfaces what requires attention and enables the right stakeholder to act, closing the loop between insight and operations.",
    chips: ["Exception surfacing", "Ownership & follow-through", "Action, not just reports"],
  },
  {
    n: "06", icon: Bot, tone: "#6366F1", title: "Automation & intelligence",
    body: "The same operating layer is designed to progress from real-time operations toward automation, AI intelligence and smart school infrastructure — built for where schools are going.",
    chips: ["Automation", "AI intelligence", "Smart infrastructure"],
  },
];

export default function HowItWorks() {
  return (
    <>
      <PageHero
        overline="How It Works"
        title="How PARTECON works in real time."
        sub="PARTECON is not simply a software application — it is a connected operating layer. Here is how school activity flows from the classroom into real-time visibility, action and intelligence."
      />

      {/* Flow band */}
      <section className="chrome py-14 border-b border-white/8" data-testid="how-flow-band">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-3">
            {flow.map((f, i) => (
              <div key={f} className="flex items-center gap-3">
                <span className="glass rounded-full px-4 py-2 text-[13px] text-white/85 font-medium whitespace-nowrap">{f}</span>
                {i < flow.length - 1 && <span className="text-[#A5B4FC]">→</span>}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Steps */}
      <section className="canvas py-20 md:py-28" data-testid="how-steps">
        <div className="relative max-w-4xl mx-auto px-5 sm:px-8">
          <div className="space-y-6">
            {steps.map((s, i) => (
              <Reveal key={s.n} delay={i * 0.04}>
                <div className="relative">
                  <div className="rounded-2xl bg-white border border-[#ECECE8] p-6 md:p-8 shadow-[0_4px_24px_-8px_rgba(16,17,23,0.05)]">
                    <div className="flex items-start gap-5">
                      <div className="shrink-0">
                        <span className="h-12 w-12 rounded-xl flex items-center justify-center" style={{ background: `${s.tone}18` }}>
                          <s.icon size={22} style={{ color: s.tone }} />
                        </span>
                        <div className="font-mono-accent text-xs text-[#9AA1AD] mt-2 text-center">{s.n}</div>
                      </div>
                      <div className="min-w-0">
                        <h3 className="font-display text-xl md:text-2xl font-semibold text-[#1A1D26] tracking-tight">{s.title}</h3>
                        <p className="text-[#6B7280] text-[15px] leading-relaxed mt-3">{s.body}</p>
                        <div className="flex flex-wrap gap-2 mt-4">
                          {s.chips.map((c) => (
                            <span key={c} className="badge-soft text-[11px] px-2.5 py-1">{c}</span>
                          ))}
                        </div>
                        {s.visual === "command" && (
                          <div className="mt-6"><Zoomable label="Principal Command Center"><CommandCenterMockup live /></Zoomable></div>
                        )}
                      </div>
                    </div>
                  </div>
                  {i < steps.length - 1 && (
                    <div className="flex justify-center py-2">
                      <motion.span initial={{ opacity: 0, y: -6 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
                        <ArrowDown className="text-[#C7C7C0]" size={22} />
                      </motion.span>
                    </div>
                  )}
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Outcome strip */}
      <section className="chrome py-16 border-t border-white/8" data-testid="how-outcome">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid sm:grid-cols-3 gap-4">
          {[
            { icon: ClipboardCheck, t: "One operational record", d: "Every class leaves a structured record — connected end to end." },
            { icon: Activity, t: "Live, not end-of-term", d: "Visibility as activity happens, not weeks later in a report." },
            { icon: Zap, t: "Insight to action", d: "The right stakeholder is enabled to act on what matters." },
          ].map((o) => (
            <div key={o.t} className="glass rounded-2xl p-6">
              <span className="h-10 w-10 rounded-lg bg-[#6366F1]/15 flex items-center justify-center"><o.icon size={18} className="text-[#A5B4FC]" /></span>
              <h4 className="font-display font-semibold text-white mt-4">{o.t}</h4>
              <p className="text-white/55 text-sm mt-1.5 leading-relaxed">{o.d}</p>
            </div>
          ))}
        </div>
      </section>

      <CTASection />
    </>
  );
}